import Log from "../Log";
import {
	default as JsfAjax,
	JsfAjaxErrorEvent,
	JsfAjaxRequestEvent,
	JsfAjaxAlwaysEvent,
	JsfAjaxEvent
} from "./JsfAjax";
import Messages from "../../components/Messages";
import AjaxStatusScreenBlocker from "../../components/AjaxStatusScreenBlocker";

export type CustomStatusElementCallback = (ev: JsfAjaxEvent, customStatusElem?: Element) => void;

type EventBeginListener = (
	event: JsfAjaxRequestEvent,
	addStatusElement: (elem: Element, onComplete?: CustomStatusElementCallback) => void,
	preventGlobalStatus: () => void
) => void;

type CustomStatusElementAndCallback = {
	statusElem: Element;
	onComplete?: CustomStatusElementCallback;
};

/**
 * An instance will be initialized immediately upon importing this class.
 * If you do need a reference, you can use `getInstance()`.
 * 
 * You can use `addEventBeginListener()` to listen for the stat of ajax events
 * and add elements to be marked with "isActiveNativeJsfAjaxEvent" style class indicating active ajax.
 * A listener can also prevent the default global status indicator from being utilized.
 * For an example see: EditContentAjaxStatus.ts
 */
export default class JsfAjaxStatus {
	private static readonly instance = new JsfAjaxStatus();
	public static readonly isActiveStyleClass = "isActiveJsfAjaxEvent";
	public static readonly globalStatusElementStyleClass = "jsfAjaxStatusGlobal";

	private activeEventCount = 0;

	private eventBeginListeners: Set<EventBeginListener> | undefined;
	private customStatusElementSets: Map<HTMLElement, Set<CustomStatusElementAndCallback>> | undefined; // The source element is the key for the set of custom elements. Each element is wrapped in an objet that provides access to the element and an optional function that is called when status is complete
	private customStatusElementEventCounts: Map<Element, number> | undefined;	// customStatusElem is the key and the value is a count. Since an element could be working as a status indicator for multiple overlapping ajax events.

	public static getInstance() {
		return JsfAjaxStatus.instance;
	}

	private constructor() {
		const jsfAjax = JsfAjax.getInstance();
		if (jsfAjax.isNativeJsfAjaxLoaded()) {
			jsfAjax.addEventListener("begin", this.onEventBegin);
			jsfAjax.addEventListener("error", this.onAjaxError);
			jsfAjax.addEventListener("always", this.onAjaxAlways);
		}
	}

	public addEventBeginListener(listener: EventBeginListener): void {
		if (this.eventBeginListeners === undefined) {
			this.eventBeginListeners = new Set<EventBeginListener>();
		}
		this.eventBeginListeners.add(listener);
	}
	public removeEventBeginListener(listener: EventBeginListener): boolean {
		if (this.eventBeginListeners === undefined) {
			return false;
		}
		return this.eventBeginListeners.delete(listener);
	}


	
	/* ================== Private ============= */

	private static getGlobalStatusElements(): HTMLCollectionOf<Element> {
		return document.getElementsByClassName(JsfAjaxStatus.globalStatusElementStyleClass);
	}


	private readonly addCustomStatusElement = (sourceElem: HTMLElement) => (customStatusElem: Element, onComplete?: CustomStatusElementCallback) => {
		if (this.customStatusElementSets === undefined) {
			this.customStatusElementSets = new Map();
			this.customStatusElementEventCounts = new Map<Element, number>();
		}
		
		let statusElements = this.customStatusElementSets.get(sourceElem);
		if (statusElements === undefined) {
			statusElements = new Set<CustomStatusElementAndCallback>();
			this.customStatusElementSets.set(sourceElem, statusElements);
		}
		const customStatusElementAndCallback: CustomStatusElementAndCallback = {
			statusElem: customStatusElem,
			onComplete: onComplete
		}
		statusElements.add(customStatusElementAndCallback);

		const currentCount = this.customStatusElementEventCounts!.get(customStatusElem);
		this.customStatusElementEventCounts!.set(
			customStatusElem,
			currentCount === undefined ? 1 : currentCount + 1
		);
	}

	private readonly onEventBegin = (ev: JsfAjaxRequestEvent) => {
		const sourceElem = ev.sourceElement;
		const potentialBlockingStatusText = sourceElem?.getAttribute("data-jsf-ajax-blocking-action");
		if (potentialBlockingStatusText !== null && potentialBlockingStatusText !== undefined) {
			if (ev.internalOptions === null) {
				ev.internalOptions = Object.create(null);
			}
			AjaxStatusScreenBlocker.enableBlockerOnRequest(ev.internalOptions, potentialBlockingStatusText, ev.sourceElement.ownerDocument)
		}

		// Notify listeners
		let isGlobalStatusPrevent = false;
		if (this.eventBeginListeners !== undefined && this.eventBeginListeners !== null) {
			const preventGlobalStatus = () => {
				isGlobalStatusPrevent = true;
			};
			this.eventBeginListeners.forEach((listener: EventBeginListener) => {
				listener(
					ev,
					this.addCustomStatusElement(sourceElem),
					preventGlobalStatus
				);
			});
		}

		// Source Element
		if (sourceElem !== null && sourceElem !== undefined) {
			sourceElem.classList.add(JsfAjaxStatus.isActiveStyleClass);

			// Enclosing form (if there is one)
			JsfAjaxStatus.getFormFor(sourceElem)?.classList.add(JsfAjaxStatus.isActiveStyleClass);
		}

		// "html" tag
		this.activeEventCount++;
		document.documentElement.classList.add(JsfAjaxStatus.isActiveStyleClass);

		// Global status element
		if (!isGlobalStatusPrevent) {
			const globalElems = JsfAjaxStatus.getGlobalStatusElements();
			for (let i = 0; i < globalElems.length; i++) {
				globalElems[i]!.classList.add(JsfAjaxStatus.isActiveStyleClass);
			}
		}

		// Mark custom status elements (added by listeners)
		if (this.customStatusElementSets !== undefined) {
			const customStatusElems = this.customStatusElementSets.get(sourceElem);
			if (customStatusElems !== undefined) {
				customStatusElems.forEach((item: CustomStatusElementAndCallback) => {
					item.statusElem.classList.add(JsfAjaxStatus.isActiveStyleClass);
				});
			}
		}
	}

	private readonly onAjaxError = (ev: JsfAjaxErrorEvent) => {
		Log.error("Error during AJAX request:", ev.message, ev);
		Messages.createDesktopMessages([ev.message ?? ""], "error");
	}

	private readonly onAjaxAlways = (ev: JsfAjaxAlwaysEvent) => {
		const sourceElem = ev.sourceElement;
		const responseXML = ev.responseXML;
		
		// Check if response is a redirect. If it is, do not REMOVE the active class from everything because there is going to be a short delay and then the browser will be redirected and we should maintain the "working" indicator until that is completed.
		if (responseXML !== undefined && responseXML !== null) {
			const partialResponse = responseXML.getElementsByTagName("partial-response")[0];
			const responseType = partialResponse?.firstChild;
			if (responseType?.nodeName === "redirect") {
				return;
			}
		}

		if (sourceElem !== undefined) {
			// Source Element
			sourceElem.classList.remove(JsfAjaxStatus.isActiveStyleClass);

			// Enclosing form (if there is one)
			JsfAjaxStatus.getFormFor(sourceElem)?.classList.remove(JsfAjaxStatus.isActiveStyleClass);
		}

		// "html" tag
		this.activeEventCount = this.activeEventCount === 0 ? 0 : this.activeEventCount - 1;
		if (this.activeEventCount === 0) {
			document.documentElement.classList.remove(JsfAjaxStatus.isActiveStyleClass);
		}

		// Global status element
		const globalElems = JsfAjaxStatus.getGlobalStatusElements();
		for (let i = 0; i < globalElems.length; i++) {
			globalElems[i]?.classList.remove(JsfAjaxStatus.isActiveStyleClass);
		}

		// Unmark custom status elements (added by listeners during the "begin" phase)
		if (this.customStatusElementSets !== undefined && sourceElem !== undefined) {
			const statusElems = this.customStatusElementSets.get(sourceElem);
			if (statusElems !== undefined) {
				statusElems.forEach((item: CustomStatusElementAndCallback) => {
					// Update tally for number of active ajax events for this element
					// If that tally is now zero remove the style class which marks active.
					const statusElem = item.statusElem;
					const newCount = (this.customStatusElementEventCounts!.get(statusElem) as number) - 1; // if statusElems is defined, then we know that the same holds true for this map
					if (newCount === 0) {
						statusElem.classList.remove(JsfAjaxStatus.isActiveStyleClass);
						if (item.onComplete !== undefined) {
							item.onComplete(ev, statusElem);
						}
						statusElems.delete(item);
						this.customStatusElementEventCounts!.delete(statusElem);
					} else {
						this.customStatusElementEventCounts!.set(statusElem, newCount);
					}
				});

				// If the set for this sourceElem is empty, delete it.
				if (statusElems.size === 0) {
					this.customStatusElementSets.delete(sourceElem);
				}
			}
		}
	}
	
	private static getFormFor(sourceElem: HTMLElement): HTMLFormElement | undefined | null {
		return (sourceElem as any).form ?? sourceElem.closest("form");
	}
}
